import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Share2 as ShareNetwork, X, MapPin } from "lucide-react";
import CaseDocuments from "@/components/CaseDocuments";
import RiskAssessment from "@/components/RiskAssessment";
import AiRecommend from "@/components/AiRecommend";

const BAND_CLASS = { low: "risk-low", medium: "risk-medium", high: "risk-high", critical: "risk-critical" };
const STATUS_CLASS = { open: "status-open", in_progress: "status-in_progress", referred: "status-referred", closed: "status-closed" };

export default function CaseDetail() {
  const { caseId } = useParams();
  const [c, setC] = useState(null);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("");
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [refModal, setRefModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/cases/${caseId}`);
    setC(data);
    setStatus(data.status);
    setLat(data.lat ?? "");
    setLng(data.lng ?? "");
  };
  useEffect(() => { load(); }, [caseId]);

  if (!c) return <div className="p-8">Loading…</div>;

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      if (status !== c.status) patch.status = status;
      if (notes) patch.notes = notes;
      if (lat !== "" && Number(lat) !== c.lat) patch.lat = Number(lat);
      if (lng !== "" && Number(lng) !== c.lng) patch.lng = Number(lng);
      if (Object.keys(patch).length) await api.patch(`/cases/${caseId}`, patch);
      setNotes("");
      await load();
    } finally { setSaving(false); }
  };

  return (
    <div className="p-8 md:p-10 max-w-[1600px] mx-auto">
      <Link to="/cases" className="text-xs overline flex items-center gap-2 mb-6 text-slate-500 hover:text-slate-900">
        <ArrowLeft size={14} /> Back to registry
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-0 border border-slate-200">
        {/* Main */}
        <div className="p-8 lg:border-r border-slate-200">
          <div className="overline">Case Record</div>
          <div className="flex items-baseline gap-4 mt-2 flex-wrap">
            <h1 className="font-heading font-extrabold text-4xl tracking-tight">{c.subject_name}</h1>
            <span className="font-mono text-slate-500">{c.case_id}</span>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <span className={`${BAND_CLASS[c.risk_band]} px-3 py-1 text-xs font-bold uppercase tracking-wider`}>
              Risk {c.risk_band} · {c.risk_score}
            </span>
            <span className={`${STATUS_CLASS[c.status]} px-3 py-1 text-xs font-bold uppercase tracking-wider`}>
              {c.status.replace("_"," ")}
            </span>
            <span className="border border-slate-300 px-3 py-1 text-xs font-bold uppercase tracking-wider">
              {c.category.replace("_"," ")}
            </span>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 border border-slate-200">
            {[
              ["Age", c.age ?? "—"],
              ["Gender", c.gender || "—"],
              ["Location", c.location || "—"],
              ["Severity", c.severity],
              ["Disability", c.has_disability ? "Yes" : "No"],
              ["Out of school", c.out_of_school ? "Yes" : "No"],
              ["Abuse reported", c.abuse_reported ? "Yes" : "No"],
              ["Income", c.household_income || "—"],
            ].map(([l, v]) => (
              <div key={l} className="p-4 border-r border-b border-slate-200">
                <div className="overline text-[9px]">{l}</div>
                <div className="mt-2 text-sm font-semibold capitalize">{String(v)}</div>
              </div>
            ))}
          </div>

          {c.description && (
            <div className="mt-8">
              <div className="overline">Description</div>
              <p className="mt-2 text-sm leading-relaxed text-slate-700 border-l-2 border-[#0033A0] pl-4">{c.description}</p>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 border border-slate-200 p-6">
            <div className="overline mb-4">Case worker actions</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="overline block mb-1">Update status</label>
                <select data-testid="update-status" value={status} onChange={e => setStatus(e.target.value)}
                  className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]">
                  <option value="open">Open</option>
                  <option value="in_progress">In progress</option>
                  <option value="referred">Referred</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-1">Add note</label>
                <input data-testid="case-note" value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder="Field observation, follow-up, etc."
                  className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]" />
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="overline block mb-1 flex items-center gap-1"><MapPin size={12}/> Latitude</label>
                <input data-testid="case-lat" type="number" step="0.0001" value={lat} onChange={e => setLat(e.target.value)}
                  placeholder="e.g. 28.6139"
                  className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0] font-mono text-sm" />
              </div>
              <div>
                <label className="overline block mb-1 flex items-center gap-1"><MapPin size={12}/> Longitude</label>
                <input data-testid="case-lng" type="number" step="0.0001" value={lng} onChange={e => setLng(e.target.value)}
                  placeholder="e.g. 77.2090"
                  className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0] font-mono text-sm" />
              </div>
              <div className="flex items-end">
                <button type="button" onClick={() => {
                  if (!navigator.geolocation) return alert("Geolocation not supported");
                  navigator.geolocation.getCurrentPosition(p => { setLat(p.coords.latitude.toFixed(6)); setLng(p.coords.longitude.toFixed(6)); });
                }} className="w-full border border-slate-300 px-4 py-3 text-xs hover:bg-slate-900 hover:text-white">
                  Use my location
                </button>
              </div>
            </div>
            <div className="mt-4 flex gap-3 justify-end">
              <button data-testid="refer-btn" onClick={() => setRefModal(true)} className="border border-slate-300 px-5 py-3 flex items-center gap-2 hover:bg-slate-900 hover:text-white">
                <ShareNetwork size={16} /> Refer to partner
              </button>
              <button data-testid="save-case" disabled={saving} onClick={save} className="bg-[#0033A0] text-white px-5 py-3 hover:bg-slate-900 disabled:opacity-60">
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
            <div className="mt-3 flex justify-end">
              <AiRecommend caseId={c.case_id} />
            </div>
          </div>

          {/* Documents */}
          <CaseDocuments caseId={c.case_id} />

          {/* Risk Assessment */}
          <RiskAssessment caseId={c.case_id} onDone={load} />

          {/* Timeline */}
          <div className="mt-8">
            <div className="overline mb-4">Timeline</div>
            <ol className="space-y-4">
              <li className="border-l-2 border-[#0033A0] pl-4">
                <div className="text-xs font-mono text-slate-500">{c.created_at?.slice(0,16).replace("T"," ")}</div>
                <div className="font-semibold text-sm">Case created by {c.created_by_email}</div>
              </li>
              {(c.notes_log || []).map((n, i) => (
                <li key={i} className="border-l-2 border-slate-300 pl-4">
                  <div className="text-xs font-mono text-slate-500">{n.at?.slice(0,16).replace("T"," ")} · {n.by} ({n.role})</div>
                  <div className="text-sm">{n.text}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Side panel */}
        <div className="p-8 bg-slate-50">
          <div className="overline mb-3">Case QR</div>
          <div className="bg-white border border-slate-200 p-4 flex items-center justify-center">
            <QRCodeSVG value={c.case_id} size={200} bgColor="#ffffff" fgColor="#0F172A" level="M" />
          </div>
          <div className="mt-3 font-mono text-xs text-center text-slate-500">{c.case_id}</div>
          <p className="mt-4 text-xs text-slate-500 leading-relaxed">
            Scan on the ISCIC Case Worker app to open this record in the field.
          </p>

          <div className="mt-8 border-t border-slate-200 pt-6">
            <div className="overline mb-2">Meta</div>
            <div className="text-xs text-slate-600 space-y-1 font-mono">
              <div>Created: {c.created_at?.slice(0,10)}</div>
              <div>Updated: {c.updated_at?.slice(0,10)}</div>
              <div>By: {c.created_by_email}</div>
            </div>
          </div>
        </div>
      </div>

      {refModal && <ReferralModal caseId={c.case_id} onClose={() => setRefModal(false)} onDone={() => { setRefModal(false); load(); }} />}
    </div>
  );
}

function ReferralModal({ caseId, onClose, onDone }) {
  const [form, setForm] = useState({ to_organization: "", to_user_email: "", reason: "", priority: "medium" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      await api.post("/referrals", { case_id: caseId, ...form, to_user_email: form.to_user_email || undefined });
      onDone();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg border border-slate-300">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div><div className="overline">Route</div><h3 className="font-heading font-extrabold text-2xl">Refer case</h3></div>
          <button onClick={onClose}><X /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div><label className="overline block mb-1">Partner organization</label>
            <input data-testid="ref-org" required value={form.to_organization} onChange={e => setForm({...form, to_organization: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]" />
          </div>
          <div><label className="overline block mb-1">Contact email (optional)</label>
            <input type="email" value={form.to_user_email} onChange={e => setForm({...form, to_user_email: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0] font-mono text-sm" />
          </div>
          <div><label className="overline block mb-1">Priority</label>
            <select data-testid="ref-priority" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
            </select>
          </div>
          <div><label className="overline block mb-1">Reason</label>
            <textarea data-testid="ref-reason" required rows="3" value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]" />
          </div>
          {err && <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-4 py-3">{String(err)}</div>}
          <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
            <button type="button" onClick={onClose} className="border border-slate-300 px-5 py-3">Cancel</button>
            <button data-testid="ref-submit" disabled={saving} className="bg-[#0033A0] text-white px-5 py-3 hover:bg-slate-900 disabled:opacity-60">{saving ? "Sending…" : "Send referral"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
